import { isValidPhoneNumber } from "libphonenumber-js";

/**
 * Client-side rules for identity, business, address and bank data (sign-up,
 * company profile and bank change requests).
 *
 * These mirror the backend's `src/common/validation/input-patterns.ts` (same
 * patterns, same messages) so users get the answer before a request is sent.
 * Keep both in sync.
 */

const EMAIL_PATTERN =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
const PERSON_NAME_PATTERN = /^[\p{L}\p{M}]+(?:[ '’.-]+[\p{L}\p{M}]+)*\.?$/u;
const BUSINESS_NAME_PATTERN = /^[\p{L}\p{N}][\p{L}\p{M}\p{N} &'’.,()/-]*$/u;
const REGISTRATION_NUMBER_PATTERN = /^[A-Za-z0-9]+(?:[-/][A-Za-z0-9]+)*$/;
const ADDRESS_LINE_PATTERN = /^[\p{L}\p{N}][\p{L}\p{M}\p{N} ,.'’#&()/-]*$/u;
const CITY_PATTERN = /^[\p{L}\p{N}][\p{L}\p{M}\p{N} '’.-]*$/u;
const POSTAL_CODE_PATTERN = /^[A-Za-z0-9]+(?:[ -][A-Za-z0-9]+)*$/;
const IBAN_PATTERN = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;

/** Maximum lengths of free-text profile and bank fields (use as the inputs' `maxLength` too). */
export const MAX_LENGTH = {
  personName: 100,
  businessName: 100,
  registrationNumber: 30,
  addressLine: 100,
  city: 50,
  postalCode: 12,
  bankName: 100,
  accountHolder: 100,
  /** 34 characters plus the spaces of the usual 4-character grouping. */
  ibanInput: 42,
} as const;

export const OTP_LENGTH = 6;

export const MIN_PASSWORD_LENGTH = 8;

/** Returns an error message, or `null` when the value is valid. */
type Validator = (value: string) => string | null;

export const validateEmail: Validator = (value) => {
  const email = value.trim();
  if (!email) return "Please enter your email address.";
  return EMAIL_PATTERN.test(email) ? null : "Please enter a valid email address.";
};

/**
 * Builds a validator for a trimmed text field: required (unless `optional`),
 * at most `max` characters, and matching `pattern`.
 */
const textField =
  (rule: {
    label: string;
    required: string;
    max: number;
    pattern: RegExp;
    message: string;
    optional?: boolean;
  }): Validator =>
  (value) => {
    const text = value.trim();
    if (!text) return rule.optional ? null : rule.required;
    if (text.length > rule.max) return `${rule.label} can be at most ${rule.max} characters.`;
    return rule.pattern.test(text) ? null : rule.message;
  };

/** `value` is the E.164 number PhoneInput emits ("+97455512345"). */
export const validatePhoneNumber: Validator = (value) => {
  if (!value) return "Please enter your phone number.";
  return isValidPhoneNumber(value) ? null : "Please enter a valid phone number.";
};

export const validateFullName = textField({
  label: "Full name",
  required: "Please enter your full name.",
  max: MAX_LENGTH.personName,
  pattern: PERSON_NAME_PATTERN,
  message: "Full name can only contain letters, spaces, hyphens, apostrophes and dots.",
});

export const validateBusinessName = textField({
  label: "Business name",
  required: "Please enter your business name.",
  max: MAX_LENGTH.businessName,
  pattern: BUSINESS_NAME_PATTERN,
  message: "Business name can only contain letters, numbers, spaces and & . , ' ( ) / -",
});

export const validateCrNumber = textField({
  label: "Commercial Registration Number",
  required: "Please enter your Commercial Registration Number.",
  max: MAX_LENGTH.registrationNumber,
  pattern: REGISTRATION_NUMBER_PATTERN,
  message: "Commercial Registration Number can only contain letters, numbers, hyphens and slashes.",
});

export const validateTaxNumber = textField({
  label: "Tax Number",
  required: "Please enter your Tax Number.",
  max: MAX_LENGTH.registrationNumber,
  pattern: REGISTRATION_NUMBER_PATTERN,
  message: "Tax Number can only contain letters, numbers, hyphens and slashes.",
});

const ADDRESS_LINE_MESSAGE = "Address can only contain letters, numbers, spaces and , . ' # & ( ) / -";

export const validateStreet = textField({
  label: "Street",
  required: "Please enter the street.",
  max: MAX_LENGTH.addressLine,
  pattern: ADDRESS_LINE_PATTERN,
  message: ADDRESS_LINE_MESSAGE,
});

export const validateStreet2 = textField({
  label: "Building / Apt / Zone",
  required: "",
  max: MAX_LENGTH.addressLine,
  pattern: ADDRESS_LINE_PATTERN,
  message: ADDRESS_LINE_MESSAGE,
  optional: true,
});

export const validateCity = textField({
  label: "City",
  required: "Please enter the city.",
  max: MAX_LENGTH.city,
  pattern: CITY_PATTERN,
  message: "City can only contain letters, numbers, spaces, hyphens, apostrophes and dots.",
});

export const validatePostalCode = textField({
  label: "Postal code",
  required: "",
  max: MAX_LENGTH.postalCode,
  pattern: POSTAL_CODE_PATTERN,
  message: "Postal code can only contain letters, numbers, spaces and hyphens.",
  optional: true,
});

export const validateBankName = textField({
  label: "Bank name",
  required: "Please enter the bank name.",
  max: MAX_LENGTH.bankName,
  pattern: BUSINESS_NAME_PATTERN,
  message: "Bank name can only contain letters, numbers, spaces and & . , ' ( ) / -",
});

export const validateAccountHolder = textField({
  label: "Account holder",
  required: "Please enter the account holder.",
  max: MAX_LENGTH.accountHolder,
  pattern: BUSINESS_NAME_PATTERN,
  message: "Account holder can only contain letters, numbers, spaces and & . , ' ( ) / -",
});

/** IBANs are stored compact and upper-case: "sa03 8000 0000 …" → "SA038000…". */
export const normalizeIban = (value: string) => value.replace(/\s+/g, "").toUpperCase();

/** ISO 7064 mod 97-10 check of a normalized IBAN. */
function hasValidIbanChecksum(iban: string): boolean {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    const digits = /\d/.test(char) ? char : String(char.charCodeAt(0) - 55);
    for (const digit of digits) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

export const validateIban: Validator = (value) => {
  const iban = normalizeIban(value);
  if (!iban) return "Please enter the IBAN.";
  if (!IBAN_PATTERN.test(iban)) {
    return "IBAN must start with a 2-letter country code and 2 check digits, followed by letters and numbers (15–34 characters).";
  }
  return hasValidIbanChecksum(iban) ? null : "This IBAN is not valid. Please check it for typos.";
};

export const validateNewPassword = (
  password: string,
  confirmation: string,
): { password?: string; confirmation?: string } | null => {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return { password: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.` };
  }
  if (password !== confirmation) {
    return { confirmation: "Passwords do not match." };
  }
  return null;
};

/**
 * Keeps only digits, capped at the OTP length — use as the value transform of
 * every one-time-code field so spaces/letters (typed or pasted) never get in.
 */
export const sanitizeOtp = (value: string) => value.replace(/\D/g, "").slice(0, OTP_LENGTH);

export const isCompleteOtp = (value: string) => value.length === OTP_LENGTH;
